import { FC, useMemo } from 'react';
import useTheme from '../../../hooks/ui/useTheme';
import { OpeningHoursInterface } from '../../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import FeedbackLabel from '../../labels/FeedbackLabel';
import WorkScheduleHelpers from '../../../services/helpers/WorkScheduleHelpers';
import i18n from 'i18next';
import { useSelector } from 'react-redux';
import useBasket from '../../../hooks/basket/useBasket';
import { StoreInterface } from '../../../store/types';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import CounterButton from '../../buttons/CounterButton';
import { Button } from '@mui/material';
import PriceComparison from '../../menu/PriceComparison';
import { IOrderBasketItem, IOrderBasketItemContent } from '.';

const OrderBasketItem: FC<IOrderBasketItem & IOrderBasketItemContent> = (props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Basket:OrderBasketItem' });

  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const { data, index, hideDiscount, editable, extra, onUpdateClick, renderDescription, getSoldOutItems } = props;

  const theme = useTheme();
  const { formatScheduleUnit } = useAppMethods();
  const { incrementItem, decrementItem } = useBasket();

  const renderWarnings = useMemo(() => {
    if (!editable) {
      return null;
    }

    if (data.is_sold_out) {
      const soldOutItems = getSoldOutItems();

      return (
        <FeedbackLabel
          message={t('feedback.item_sold_out', {
            count: soldOutItems.count(),
            items: soldOutItems.implode(', '),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      );
    }

    return (
      !data.is_available && (
        <FeedbackLabel
          message={t('feedback.item_unavailable', {
            schedule: WorkScheduleHelpers.formatSchedule(
              data?.menu?.schedule ?? company?.work_schedule,
              (item: OpeningHoursInterface) => `${formatScheduleUnit(item.open)} - ${formatScheduleUnit(item.close)}`,
              (item: OpeningHoursInterface) =>
                `${formatScheduleUnit(item.open, { day: true, time: true })} - ${formatScheduleUnit(item.close, { day: true, time: true })}`,
            ),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      )
    );
  }, [editable, data, company?.work_schedule, i18n.language]);

  return (
    <>
      <div className="w-full rounded-lg bg-brand-card space-y-3 bordered border-xs p-sm border-brand-border">
        <div className="flex space-x-md">
          <div className="space-y-1">
            <h6
              className="break-words line-clamp-2 font-normal"
              style={{
                wordBreak: 'break-word',
              }}
            >
              {data.entry?.name}
            </h6>
            {data.note && <p className="text-xs text-brand-text-secondary">{data.note}</p>}
            {renderDescription()}
            {editable && (
              <Button
                onClick={onUpdateClick}
                disabled={!editable || !onUpdateClick}
                className="!min-w-fit !ps-0 !pe-0 h-[10px] !capitalize !font-normal !text-xs !text-brand-primary"
              >
                {t('update')}
              </Button>
            )}
            {editable && (
              <CounterButton
                count={data.quantity}
                increase={() => (!data.is_free ? incrementItem(index) : null)}
                decrease={() => decrementItem(index)}
                allowZero
                iconColor={theme.color.brand.icon.color}
                classes={{
                  container:
                    'h-8 min-w-[100px] !w-fit flex justify-between items-center border-xs border-mobile-secondary px-sm py-xs rounded-lg !mt-sm shadow-none',
                  quantity: 'text-sm font-semibold mx-lg text-brand-text-secondary',
                  buttons: 'h-3 w-3',
                }}
              />
            )}
            {renderWarnings}
          </div>
        </div>
        <div className="flex w-full justify-between gap-sm">
          <div>
            <PriceComparison
              className="self-start text-brand-text-secondary"
              originalPrice={data.original_price}
              actualPrice={data.actual_price}
              hideDiscount={hideDiscount}
              originalPriceStyle={{
                fontSize: theme.font.size.sm,
              }}
              actualPriceStyle={{
                fontSize: theme.font.size.sm,
                color: 'black',
              }}
            />
          </div>
          {!editable && <h6 className="text-brand-primary text-end">{data.quantity}x</h6>}
        </div>
        {!!data.badges?.length &&
          data.badges.map((badge) => (
            <p
              key={badge}
              className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium"
            >
              {badge}
            </p>
          ))}
        {extra}
      </div>
    </>
  );
};

export default OrderBasketItem;
